import { useEffect, useRef, useState } from "react";

/** Nearest scrollable ancestor, or null if nothing scrolls. */
function scrollParent(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
}

/**
 * A page heading, plus a compact bar that takes its place once it scrolls away.
 *
 * Three bugs shaped this component. Each is a trap the next person will fall
 * into again if the structure is simplified back.
 *
 * 1. **A transformed ancestor breaks sticky.** `.callisto-fade-in` wraps every
 *    page and animated a transform with `both` fill, so the wrapper kept
 *    `transform: translateY(0)` forever. A transformed ancestor is a containing
 *    block, so headings resolved against it instead of the scrollport and came
 *    adrift mid-list. The keyframes animate opacity only. If pinning breaks
 *    again, look for a new transform on an ancestor first.
 *
 * 2. **Sticky resolves against the scroll container's *content* box.** While
 *    <main> carried `py-10`, `top: 0` pinned 40px down and left that padding
 *    strip as a live window onto the scrolling rows. AppShell now keeps the
 *    vertical padding on the inner wrapper. Main keeps the horizontal padding,
 *    which the bar bleeds into (`-mx-4 md:-mx-8`) and repaints.
 *
 * 3. **A collapsing sticky band feeds back into scroll position.** The first
 *    working version was one sticky element that dropped its badge and subtitle
 *    when pinned, shrinking from 105px to 68px. That 37px came out of the
 *    document, and Chrome's scroll anchoring compensated by pulling scrollTop
 *    back 37px — under the pin threshold, so the band expanded, so scrollTop
 *    went forward again. Measured: scrollTop jumping 36 → 5 → 48 → 17 → 60 on
 *    consecutive wheel ticks. On screen it reads as the heading shaking and the
 *    text smudging.
 *
 *    The fix is structural, and it is the only reason this renders two elements
 *    instead of one: **nothing here may change the document's height.** The
 *    heading below is plain in-flow content whose size never varies. The
 *    compact bar lives in a zero-height sticky rail and is absolutely
 *    positioned, so showing and hiding it cannot move a single pixel of layout.
 *    Scroll anchoring then has nothing to compensate for and the loop cannot
 *    form. Do not "simplify" this by making the heading itself sticky and
 *    conditionally rendering its parts — that is the bug.
 *
 * The observer watches the in-flow heading, never the bar. The heading's size
 * is fixed and the bar cannot affect it, so there is no path from the pinned
 * state back to the thing that decides the pinned state.
 */
export default function StickyHeading({
  icon: Icon,
  label,
  title,
  subtitle,
  actions = null,
  dense = false,
  testId,
}) {
  const headingRef = useRef(null);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const heading = headingRef.current;
    // jsdom has no IntersectionObserver; the bar simply never appears there,
    // which is correct — nothing is scrolling.
    if (!heading || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => setPinned(!entry.isIntersecting),
      { root: scrollParent(heading), threshold: 0 }
    );
    observer.observe(heading);
    return () => observer.disconnect();
  }, []);

  const badge = label && (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-callisto-gold/10 text-callisto-gold font-bold uppercase tracking-[0.2em] ${
        dense ? "px-2.5 py-0.5 text-[10px] shrink-0" : "px-3 py-1 text-[11px]"
      }`}
    >
      {Icon && <Icon size={dense ? 11 : 12} />}
      {label}
    </span>
  );

  return (
    <>
      <div ref={headingRef} data-testid={testId} data-pinned={pinned ? "true" : "false"}>
        {dense ? (
          // One baseline row, for pages whose content has its own vertical
          // budget — the attendance month grid has to fit a 720px laptop.
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {badge}
            <h1 className="text-xl md:text-2xl font-bold text-callisto-navy tracking-tight">
              {title}
            </h1>
            {subtitle && <p className="text-xs text-callisto-muted">{subtitle}</p>}
            {actions && <div className="ml-auto shrink-0">{actions}</div>}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div className="min-w-0">
              {badge}
              <h1 className="mt-3 text-3xl md:text-4xl font-bold text-callisto-navy tracking-tight">
                {title}
              </h1>
              {subtitle && <p className="mt-1 text-callisto-body">{subtitle}</p>}
            </div>
            {actions && <div className="shrink-0">{actions}</div>}
          </div>
        )}
      </div>

      {/* Zero-height rail. `margin-top: 0` inline because pages lay their
          children out with `space-y-*`, which would otherwise hand this a 24px
          margin and push everything below it down. Inline beats the utility
          without needing an `!important`. */}
      <div className="sticky top-0 z-20 h-0 -mx-4 md:-mx-8" style={{ marginTop: 0 }}>
        <div
          // Decorative: the real <h1> is still in the DOM above, just scrolled
          // out. Announcing this too would read the page title twice.
          aria-hidden="true"
          data-testid={testId ? `${testId}-pinned` : undefined}
          data-visible={pinned ? "true" : "false"}
          className={`absolute inset-x-0 top-0 px-4 md:px-8 py-3 bg-callisto-app transition-opacity duration-150 ${
            pinned
              ? "opacity-100 shadow-[0_1px_0_0_rgba(15,23,42,0.08)]"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <p className="text-lg md:text-xl font-bold text-callisto-navy tracking-tight truncate">
            {title}
          </p>
        </div>
      </div>
    </>
  );
}
